import React, { useState } from 'react';
import {
    Box,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles((theme) => ({
    sidebar: {
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        overflowY: 'auto',
    },
    sidebarTitle: {
        marginBottom: theme.spacing(1),
    },
    referenceItem: {
        marginBottom: theme.spacing(1),
    },
    referenceToggle: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        padding: theme.spacing(0.5, 0),
    },
    referenceToggleLabel: {
        flex: 1,
        fontWeight: 500,
    },
    referenceContent: {
        padding: theme.spacing(1, 0.5, 1.5, 0.5),
    },
    referenceSubsection: {
        marginTop: theme.spacing(1.5),
    },
    referenceCode: {
        display: 'inline-block',
        padding: theme.spacing(0.25, 0.5),
        backgroundColor: theme.palette.action.hover,
        borderRadius: 3,
        fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
        fontSize: 12,
    },
    referenceExamples: {
        marginTop: theme.spacing(0.5),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
    },
    reasonCodeList: {
        listStyle: 'none',
        margin: theme.spacing(0.75, 0, 0, 0),
        padding: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.default,
        maxHeight: 320,
        overflowY: 'auto',
    },
    reasonCodeItem: {
        padding: theme.spacing(0.5, 1),
        fontSize: 12,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    warehouseTableContainer: {
        marginTop: theme.spacing(0.75),
        '& .MuiTableCell-root': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(0.75),
            fontSize: 12,
        },
    },
}));

function ReferenceItem({ id, title, defaultOpen = false, children }) {
    const classes = useStyles();
    const [open, setOpen] = useState(defaultOpen);
    const toggle = () => setOpen((prev) => !prev);
    const contentId = `${id}_content`;

    return (
        <Box className={classes.referenceItem}>
            <Box
                className={classes.referenceToggle}
                onClick={toggle}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={contentId}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <IconButton
                    size="small"
                    aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
                    aria-expanded={open}
                >
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="body2" className={classes.referenceToggleLabel}>
                    {title}
                </Typography>
            </Box>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box id={contentId} className={classes.referenceContent}>
                    {children}
                </Box>
            </Collapse>
            <Divider />
        </Box>
    );
}

function PurchaseOrderLookupReference() {
    const classes = useStyles();
    return (
        <>
            <Typography variant="body2" color="textSecondary">
                Purchase Orders in NetSuite follow strict naming conventions based on the type of PO and
                the originating Fulfillment Center.
            </Typography>

            <Box className={classes.referenceSubsection}>
                <Typography variant="subtitle2">Food POs</Typography>
                <Typography variant="body2">
                    Format: <span className={classes.referenceCode}>{'<FC prefix>-<Jalapeno PO ID>'}</span>
                </Typography>
                <Box className={classes.referenceExamples}>
                    {`LAX1-78781\nSAT1-78782\nBWI1-78783\nCHI1-78784`}
                </Box>
            </Box>

            <Box className={classes.referenceSubsection}>
                <Typography variant="subtitle2">Packaging POs</Typography>
                <Typography variant="body2">
                    Always prefixed with <span className={classes.referenceCode}>P-</span>.
                </Typography>
                <Typography variant="body2">
                    Format:{' '}
                    <span className={classes.referenceCode}>
                        {'P-<FC prefix>-<Jalapeno Packaging PO ID>'}
                    </span>
                </Typography>
                <Box className={classes.referenceExamples}>
                    {`P-LAX1-12342\nP-SAT1-12343\nP-BWI1-12344\nP-CHI1-12345`}
                </Box>
            </Box>

            <Box className={classes.referenceSubsection}>
                <Typography variant="subtitle2">Freight POs / Trucks</Typography>
                <Typography variant="body2">
                    Always prefixed with <span className={classes.referenceCode}>T-</span>.
                </Typography>
                <Typography variant="body2">
                    Format:{' '}
                    <span className={classes.referenceCode}>{'T-<FC prefix>-<Truck ID>'}</span>
                </Typography>
                <Box className={classes.referenceExamples}>
                    {`T-LAX1-14342\nT-SAT1-14343\nT-BWI1-14344\nT-CHI1-14345`}
                </Box>
            </Box>
        </>
    );
}

const INVENTORY_ADJUSTMENT_REASON_CODES = [
    'Produce Case Yield Variance',
    'Produce Damaged - Carrier',
    'Grocery Damaged - Carrier',
    'Repack/Redelivery',
    'DoorDash Packaging Transfer',
    'DoorDash - Packed Sales',
    'Warehouse Supplies Use',
    'GoPuff Packaging Transfer',
    'Go Puff - Packed Sales',
    'FBM Transfer',
    'Packed Activity - Non-Produce',
    'Packed Activity - Conventional',
    'Packed Activity - Organic',
    'Intercompany Transfers (IC)',
    'Produce Prior Period Receiving Correction',
    'Produce Spoilage/Quality Issues - FIFO',
    'Produce Spoilage/Quality Issues - Expired/BBD',
    'Produce Rollover/Moldy/Rotten',
    'Produce Obsolete/Expected Spoilage - Waste',
    'Produce Obsolete/Expected Spoilage - Donation',
    'Produce Daily Count',
    'Grocery Obsolete - Thawed frozen grocery',
    'Grocery Prior Period Receiving Correction',
    'Grocery Donate',
    'Grocery Damaged',
    'Grocery Moldy/Rotten',
    'Grocery Expired-FIFO',
    'Grocery Expired',
    'Grocery Cycle Count',
    'Marketing Materials',
    'Produce Substitution',
    'Produce Prep Scrap',
    'Packaging Use',
    'Damaged',
    'FC to FC Transfer',
    'Spoilage/Quality Issues - Waste',
    'Spoilage/Quality Issues - Donation',
    'Expired/Obsolete - Waste',
    'Expired/Obsolete - Donation',
    'Excess Inventory',
    'Copacking Adjustment',
    'Grocery/Produce Vendor Credit Requested (Bad Product)',
];

const NETSUITE_WAREHOUSE_NAMES = [
    { fcName: '200 - LAX', id: 3 },
    { fcName: '400 - CHI', id: 6 },
    { fcName: '500 - SAT', id: 8 },
    { fcName: '600 - BWI', id: 9 },
];

function NetSuiteWarehouseNamesReference() {
    const classes = useStyles();
    return (
        <>
            <Typography variant="body2" color="textSecondary">
                Fulfillment center display names and NetSuite warehouse identifiers.
            </Typography>
            <TableContainer
                component={Paper}
                className={classes.warehouseTableContainer}
                variant="outlined"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>FC Name</TableCell>
                            <TableCell align="right">ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {NETSUITE_WAREHOUSE_NAMES.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.fcName}</TableCell>
                                <TableCell align="right">{row.id}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

function InventoryAdjustmentReasonCodesReference() {
    const classes = useStyles();
    return (
        <>
            <Typography variant="body2" color="textSecondary">
                Use these reason codes when querying or referencing inventory adjustments in NetSuite.
                Names must match exactly.
            </Typography>
            <ul className={classes.reasonCodeList}>
                {INVENTORY_ADJUSTMENT_REASON_CODES.map((code) => (
                    <li key={code} className={classes.reasonCodeItem}>
                        {code}
                    </li>
                ))}
            </ul>
        </>
    );
}

function NetSuiteAIReferencesSidebar() {
    const classes = useStyles();
    return (
        <aside className={classes.sidebar} aria-label="NetSuite AI references">
            <Typography variant="h6" className={classes.sidebarTitle}>
                References
            </Typography>
            <Divider />
            <Box mt={1}>
                <ReferenceItem
                    id="NetSuiteAI_reference_purchase_order_lookup"
                    title="Purchase Order lookup"
                >
                    <PurchaseOrderLookupReference />
                </ReferenceItem>
                <ReferenceItem
                    id="NetSuiteAI_reference_inventory_adjustment_reason_codes"
                    title="Inventory adjustment reason codes"
                >
                    <InventoryAdjustmentReasonCodesReference />
                </ReferenceItem>
                <ReferenceItem
                    id="NetSuiteAI_reference_warehouse_names"
                    title="NetSuite Warehouse Names"
                >
                    <NetSuiteWarehouseNamesReference />
                </ReferenceItem>
            </Box>
        </aside>
    );
}

export { NetSuiteAIReferencesSidebar };
